import {
  engineAxiosClient,
  getQuantitySelection,
  getSizeSelection,
} from "@providers/BasketProvider/helpers";
import {
  AttributeValueType,
  EngineResponse,
  ProductType,
  SanityProductPreset,
  TransportPriority,
} from "@sourceful/shared-types";
import { generateEngineMetadata } from "@sourceful/shared-utils/engine-utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getEngineVersion } from "../../lib/getEngineVersion";

export interface UsePresetDetailStateProps {
  initialPreset: SanityProductPreset;
  initialEngineResponse: EngineResponse | null;
  productType: ProductType;
}

export const usePresetDetailState = ({
  initialPreset,
  initialEngineResponse,
  productType,
}: UsePresetDetailStateProps) => {
  const [preset, setPreset] = useState<SanityProductPreset>(initialPreset);

  // sync derived state
  useEffect(() => {
    setPreset(initialPreset);
  }, [initialPreset]);

  const quantityAttribute = useMemo(() => {
    if (!preset?.attributes) return;

    const qtySelection = getQuantitySelection(preset.attributes);

    return qtySelection?.attribute;
  }, [preset?.attributes]);

  const sizeAttribute = useMemo(() => {
    if (!preset?.attributes) return;

    const sizeSelection = getSizeSelection(preset.attributes);

    return sizeSelection?.attribute;
  }, [preset?.attributes]);

  // we don't actually need to fetch from engine as only qty ever changes and the prices are available in the one_choice_options
  // if we ever wanted to change any other factor that is not accounted for in one_choice_options (like transport priority, postcode then we would have to fetch from engine again)
  const [engineMetadata, setEngineMetadata] =
    useState<EngineResponse | null>(initialEngineResponse);

  // sync derived state
  useEffect(() => {
    setEngineMetadata(initialEngineResponse);
  }, [initialEngineResponse]);

  const fetchEngineMetadata = useCallback(async () => {
    if (productType === ProductType.RFQ) return;

    if (!quantityAttribute) {
      console.error(`Missing quantity attribute for preset ${preset.slug} (id: ${preset.id})`);
      return;
    }

    if (!sizeAttribute) {
      console.error(`Missing size attribute for preset ${preset.slug} (id: ${preset.id})`);
      return;
    }

    const engineMetadata = await generateEngineMetadata({
      client: engineAxiosClient,
      attributes: preset.attributes,
      baseProductId: preset.baseProductId,
      options: {
        split_delivery_quantities: undefined,
        one_step_change_attributes: [quantityAttribute.id, sizeAttribute.id],
        transportPriority: preset.defaultTransportPriority ?? TransportPriority.PRICE,
      },
      engineVersion: getEngineVersion(),
    });

    setEngineMetadata(engineMetadata);
  }, [preset, quantityAttribute, sizeAttribute, productType]);

  useEffect(() => {
    if (productType === ProductType.RFQ) return;

    // Only fetch again if we don't have the initial engine metadata and live priced product
    if (initialEngineResponse === null) fetchEngineMetadata();
  }, [fetchEngineMetadata, initialEngineResponse, preset, productType]);

  const updatePresetQuantity = useCallback(
    (value: AttributeValueType) => {
      if (!quantityAttribute) {
        console.error(`Missing quantity attribute for preset ${preset.slug} (id: ${preset.id})`);
        return;
      }
      const valueCheck = quantityAttribute?.options.find(options => options.value.id === value.id);
      if (!valueCheck) {
        console.error(`Invalid value for qty attribute ${quantityAttribute.id}`);
        return;
      }

      if (
        engineMetadata &&
        engineMetadata.one_choice_options[quantityAttribute.id] &&
        engineMetadata.one_choice_options[quantityAttribute.id][value.id]
      ) {
        const updatedEngineResult: EngineResponse | null = {
          ...engineMetadata,
          results: engineMetadata.one_choice_options[quantityAttribute.id][value.id],
        };

        setEngineMetadata(updatedEngineResult);
      } else {
        console.error(
          `Missing engine metadata for value ${value.id} (quantity attribute ${quantityAttribute.id})`
        );
      }

      setPreset(prev => {
        return {
          ...prev,
          attributes: {
            ...prev.attributes,
            [quantityAttribute.id]: {
              attribute: quantityAttribute,
              value: value,
            },
          },
        };
      });
    },
    [quantityAttribute, engineMetadata, preset]
  );

  const updatePresetSize = useCallback(
    (value: AttributeValueType, isCustomSize: boolean) => {
      console.log("updatePresetSize", { value, preset });

      if (!sizeAttribute) {
        console.error(`Missing size attribute for preset ${preset.slug} (id: ${preset.id})`);
        return;
      }

      if (!isCustomSize) {
        const valueCheck = sizeAttribute?.options.find(options => options.value.id === value.id);

        if (!valueCheck) {
          console.error(`Invalid value for size attribute ${sizeAttribute.id}`);
          return;
        }
      }

      setPreset(prev => {
        return {
          ...prev,
          attributes: {
            ...prev.attributes,
            [sizeAttribute.id]: {
              attribute: sizeAttribute,
              value: value,
            },
          },
        };
      });
    },
    [preset, sizeAttribute]
  );

  return {
    preset,
    engineMetadata,
    updatePresetQuantity,
    updatePresetSize,
    fetchEngineMetadata,
  };
};
